import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Send, Trash2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { cn } from '@/utils/cn';
import type { PlanVisitGroup as PlanVisitGroupType } from '@/hooks/useTreatmentPlans';
import { Button } from '@/components/ui/Button';
import { useReorderPlanVisit, useUpdatePlanStatus } from '@/hooks/useTreatmentPlans';
import type { EnrichedPlanItem, PlanStatus } from '@/hooks/useTreatmentPlans';
import { PlanItemRow, PlanItemRowHeader } from './PlanItemRow';
import toast from 'react-hot-toast';

// ── Status badge ───────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<PlanStatus, string> = {
  proposed: 'bg-ds-amber-tint text-ds-amber-ink text-ds-amber',
  accepted: 'bg-ds-teal-tint text-ds-teal',
  scheduled: 'bg-ds-teal-tint text-ds-teal',
  completed: 'bg-ds-green-tint text-ds-green-ink',
  cancelled: 'bg-ds-neutral-tint text-ds-muted bg-ds-sunk',
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface PlanVisitGroupProps {
  group: PlanVisitGroupType;
  patientId: string;
  clinicId: string;
  allVisitNumbers: number[];
  onEditRequest: (item: EnrichedPlanItem) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PlanVisitGroup({
  group,
  patientId,
  clinicId,
  onEditRequest,
}: PlanVisitGroupProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  const reorder = useReorderPlanVisit();
  const updateStatus = useUpdatePlanStatus();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Determine target visit from over.id (could be item id or group marker)
    // For inter-group drag, user drops onto the group header element
    // We encode target visit as "group-{visit_number}" in the droppable id
    const overId = String(over.id);
    if (overId.startsWith('group-')) {
      const targetVisit = parseInt(overId.replace('group-', ''), 10);
      if (!isNaN(targetVisit)) {
        try {
          await reorder.mutateAsync({
            patientId,
            planId: String(active.id),
            newVisitNumber: targetVisit,
          });
        } catch {
          toast.error(t('common.error'));
        }
      }
    }
  };

  const handleSendQuote = () => {
    // TODO: Parse plan items in quotes module NewQuote when TAND-quote integration lands
    navigate(`/quotes/new?patientId=${patientId}&fromPlanVisit=${group.visit_number}`);
  };

  const handleDeleteVisit = async () => {
    if (
      !confirm(
        t('dental.plan.actions.confirmDeleteVisit', {
          defaultValue: `Vil du slette hele Besøg ${group.visit_number}? Alle elementer annulleres.`,
          n: group.visit_number,
        }),
      )
    )
      return;

    try {
      await Promise.all(
        group.items.map((item) =>
          updateStatus.mutateAsync({
            patientId,
            planId: item.id,
            status: 'cancelled',
          }),
        ),
      );
      toast.success(t('dental.plan.actions.deleteVisit'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const statusKey = group.status as PlanStatus;

  return (
    <div className="rounded-xl border border-ds-line overflow-hidden">
      {/* Group header */}
      <div
        className={cn(
          'flex items-center gap-2 px-4 py-3 bg-ds-sunk',
          'border-b border-ds-line',
        )}
      >
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-ds-muted flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-ds-muted flex-shrink-0" />
          )}
          <span className="text-sm font-semibold text-ds-text">
            {t('dental.plan.visit', { n: group.visit_number })}
          </span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium ml-1',
              STATUS_BADGE[statusKey] ?? STATUS_BADGE['proposed'],
            )}
          >
            {t(`dental.plan.status.${statusKey}`, { defaultValue: statusKey })}
          </span>
          {group.items.some((i) => i.planned_date) && (
            <span className="rounded-full bg-ds-neutral-tint px-2 py-0.5 text-xs text-ds-muted">
              {group.items.find((i) => i.planned_date)?.planned_date}
            </span>
          )}
        </button>

        {/* Subtotal */}
        <span className="text-sm font-semibold text-ds-text mr-2">
          {group.subtotal > 0 ? `${group.subtotal.toLocaleString('da-DK')} kr` : ''}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="secondary"
            onClick={handleSendQuote}
            title={t('dental.plan.actions.sendQuote')}
          >
            <Send />
            <span className="hidden sm:inline">{t('dental.plan.actions.sendQuote')}</span>
          </Button>
          <Button
            type="button"
            variant="danger"
            size="icon"
            onClick={handleDeleteVisit}
            title={t('dental.plan.actions.deleteVisit')}
          >
            <Trash2 />
          </Button>
        </div>
      </div>

      {/* Items */}
      {isOpen && (
        <div className="p-2 space-y-0.5 bg-ds-card">
          <PlanItemRowHeader />
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={group.items.map((i) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              {group.items.map((item) => (
                <PlanItemRow
                  key={item.id}
                  item={item}
                  patientId={patientId}
                  clinicId={clinicId}
                  onEditRequest={onEditRequest}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Subtotal row */}
          {group.subtotal > 0 && (
            <div className="flex items-center justify-end pt-1.5 px-3 border-t border-ds-line mt-1">
              <span className="text-xs text-ds-muted mr-2">
                Subtotal besøg {group.visit_number}:
              </span>
              <span className="text-sm font-semibold text-ds-text">
                {group.subtotal.toLocaleString('da-DK')} kr
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
